import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { CloudActions } from '@portal/portal-core';
import { SuperAdminActions } from 'ducks/super-client';
import type { LicenseViewMode } from 'ducks/super-client/actions';
import { TOOL_BUTTON_NAMES } from 'constants/super-admin';
import {
  selectIndividualLicenseViewMode,
  selectInstitutionalLicenseViewMode,
  selectLicenseSearchError,
  selectLicenseSearchHitCount,
  selectLicenseSearchLimit,
  selectLicenseSearchLoading,
  selectLicenseSearchRows,
  selectLicenseSearchTotal,
} from 'selectors/super-admin';
import { useAppDispatch } from 'store/hooks';

// Fixed rather than user-configurable: every page change re-fetches from the server, so a small
// page keeps that fetch cheap. It counts cost centers, not license rows (a cost center commonly
// carries 2-3 products), so the row count per page varies.
export const SEARCH_PAGE_SIZE = 10;

// Search queries shorter than this are not dispatched (same minimum as the Users search).
const MIN_SEARCH_LENGTH = 3;

const viewModeSelectors: Record<string, typeof selectInstitutionalLicenseViewMode> = {
  [TOOL_BUTTON_NAMES.INSTITUTION_LICENSE]: selectInstitutionalLicenseViewMode,
  [TOOL_BUTTON_NAMES.INDIVIDUAL_LICENSE]: selectIndividualLicenseViewMode,
};

interface UseLicenseSearchResult<Row> {
  mode: LicenseViewMode;
  setMode: (newMode: LicenseViewMode) => void;
  searchString: string;
  setSearchString: (value: string) => void;
  productNames: string[];
  setProductNames: (names: string[]) => void;
  offset: number;
  setOffset: (offset: number) => void;
  searchRows: Row[];
  searchTotal: number;
  searchLimit: number;
  searchLoading: boolean;
  searchError: string | null;
  searchHitCount: number;
}

// State and effects shared by the Institutional and Individual license pages' search view.
// `tool` is the SuperAdmin tool name the page's view mode is stored under, `costCenterType` the
// cost center type the search is scoped to, and `onFirstLegacyVisit` starts the page's
// full-dataset sync.
export const useLicenseSearch = <Row>(
  tool: string,
  costCenterType: string,
  onFirstLegacyVisit: () => void,
): UseLicenseSearchResult<Row> => {
  const dispatch = useAppDispatch();

  // The view mode lives only in Redux so every consumer (both pages and the header keyword
  // search) agrees on it across unmounts.
  const mode = useSelector(viewModeSelectors[tool]);
  const setMode = (newMode: LicenseViewMode): void => {
    dispatch(SuperAdminActions.setLicenseViewMode(tool, newMode));
    if (newMode === 'legacy') {
      // An empty search slice makes selectCurrentLicense/selectCurrentLicenseDetails's
      // `{...legacyLicenses, ...searchLicenses}` merge resolve to legacy data while in the
      // legacy view, so a legacy edit cannot lose to stale search-derived data on an id collision.
      dispatch(SuperAdminActions.clearLicenseSearch());
    }
  };

  const [searchString, setSearchString] = useState('');
  const [productNames, setProductNames] = useState<string[]>([]);
  const [offset, setOffset] = useState(0);
  // Resets the page offset synchronously during render (React's "adjusting state when a prop
  // changes" pattern) rather than in a separate effect. A reset effect would run after the
  // dispatch effect below has already fired with the stale offset, then trigger a second render
  // and a second, duplicate request with the corrected offset.
  const searchKeyRef = useRef({ searchString, productNames });
  if (
    searchKeyRef.current.searchString !== searchString ||
    searchKeyRef.current.productNames !== productNames
  ) {
    searchKeyRef.current = { searchString, productNames };
    if (offset !== 0) {
      setOffset(0);
    }
  }

  const searchRows = useSelector(selectLicenseSearchRows) as Row[];
  const searchTotal = useSelector(selectLicenseSearchTotal);
  const searchLimit = useSelector(selectLicenseSearchLimit) || SEARCH_PAGE_SIZE;
  const searchLoading = useSelector(selectLicenseSearchLoading);
  const searchError = useSelector(selectLicenseSearchError);
  const searchHitCount = useSelector(selectLicenseSearchHitCount);

  // The full-dataset sync only starts the first time the page shows the legacy view, so the
  // default search view never pays for it. The ref keeps a later return to legacy from
  // starting a second sync.
  const legacySyncStartedRef = useRef(false);
  useEffect(() => {
    if (mode === 'legacy' && !legacySyncStartedRef.current) {
      legacySyncStartedRef.current = true;
      onFirstLegacyVisit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (mode !== 'search') {
      return;
    }
    // An empty or too-short box clears results instead of running a query: an empty query would
    // browse everything before the SuperAdmin has typed anything, and without the clear a
    // response for a longer earlier query would stay latest and keep its rows on screen under
    // the minimum-length message.
    if (searchString.trim().length < MIN_SEARCH_LENGTH) {
      dispatch(SuperAdminActions.clearLicenseSearch());
      return;
    }
    dispatch(
      CloudActions.elasticsearchCostCenterSearch(
        searchString,
        costCenterType,
        productNames,
        searchLimit,
        offset,
      ),
    );
    // searchLimit is read from the previous response and must not retrigger the search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, searchString, productNames, offset]);

  return {
    mode,
    setMode,
    searchString,
    setSearchString,
    productNames,
    setProductNames,
    offset,
    setOffset,
    searchRows,
    searchTotal,
    searchLimit,
    searchLoading,
    searchError,
    searchHitCount,
  };
};
